/**
 * Local JSON database client.
 *
 * Drop-in replacement for the Supabase client for read-only reference data.
 * Supports the same query chain used across the app:
 *   db.from('table').select('a, b').eq('field', value).order('field')
 *   db.from('table').select('*').eq('field', value).maybeSingle()
 *
 * Returns the same { data, error } shape as Supabase so call-sites
 * only need their import changed, not their logic.
 */

import occupationsRaw from "@/data/occupations.json";
import skillsRaw from "@/data/skills.json";
import weaponsRaw from "@/data/weapons.json";
import gaslightOccupationsRaw from "@/data/gaslight-occupations.json";
import gaslightSkillsRaw from "@/data/gaslight-skills.json";
import gaslightWeaponsRaw from "@/data/gaslight-weapons.json";
import modernOccupationsRaw from "@/data/modern-occupations.json";
import modernSkillsRaw from "@/data/modern-skills.json";
import modernWeaponsRaw from "@/data/modern-weapons.json";
import regencyOccupationsRaw from "@/data/regency-occupations.json";
import regencySkillsRaw from "@/data/regency-skills.json";
import regencyWeaponsRaw from "@/data/regency-weapons.json";
import westernOccupationsRaw from "@/data/western-occupations.json";
import westernSkillsRaw from "@/data/western-skills.json";
import westernWeaponsRaw from "@/data/western-weapons.json";
import darkagesOccupationsRaw from "@/data/darkages-occupations.json";
import darkagesSkillsRaw from "@/data/darkages-skills.json";
import darkagesWeaponsRaw from "@/data/darkages-weapons.json";
import invictusOccupationsRaw from "@/data/invictus-occupations.json";
import invictusSkillsRaw from "@/data/invictus-skills.json";
import invictusWeaponsRaw from "@/data/invictus-weapons.json";

type Row = Record<string, unknown>;

export interface SkillRow extends Row {
  label: string;
  base_value: number | null;
}

const TABLES: Record<string, Row[]> = {
  occupation_database: occupationsRaw as Row[],
  skills: skillsRaw as Row[],
  weapon_table: weaponsRaw as Row[],
  gaslight_occupations: gaslightOccupationsRaw as Row[],
  gaslight_skills: gaslightSkillsRaw as Row[],
  gaslight_weapons: gaslightWeaponsRaw as Row[],
  modern_occupations: modernOccupationsRaw as Row[],
  modern_skills: modernSkillsRaw as Row[],
  modern_weapons: modernWeaponsRaw as Row[],
  regency_occupations: regencyOccupationsRaw as Row[],
  regency_skills: regencySkillsRaw as Row[],
  regency_weapons: regencyWeaponsRaw as Row[],
  western_occupations: westernOccupationsRaw as Row[],
  western_skills: westernSkillsRaw as Row[],
  western_weapons: westernWeaponsRaw as Row[],
  darkages_occupations: darkagesOccupationsRaw as Row[],
  darkages_skills: darkagesSkillsRaw as Row[],
  darkages_weapons: darkagesWeaponsRaw as Row[],
  invictus_occupations: invictusOccupationsRaw as Row[],
  invictus_skills: invictusSkillsRaw as Row[],
  invictus_weapons: invictusWeaponsRaw as Row[],
};

// ---------------------------------------------------------------------------
// Field-list parser
// Handles: '*', 'a, b', '"Quoted Field", c'
// ---------------------------------------------------------------------------
function parseFields(select: string): string[] | null {
  if (select.trim() === "*") return null;
  return select.split(",").map((f) => f.trim().replace(/^"|"$/g, ""));
}

function projectRow(row: Row, fields: string[] | null): Row {
  if (!fields) return row;
  return Object.fromEntries(fields.map((f) => [f, row[f] ?? null]));
}

// ---------------------------------------------------------------------------
// Query builder
// ---------------------------------------------------------------------------
class QueryBuilder<T extends Row = Row> {
  private rows: T[];
  private fields: string[] | null = null;
  private eqFilters: Array<{ field: string; value: unknown }> = [];
  private orderField: string | null = null;

  constructor(rows: T[]) {
    this.rows = rows;
  }

  select(fields: string): this {
    this.fields = parseFields(fields);
    return this;
  }

  eq(field: string, value: unknown): this {
    this.eqFilters.push({ field, value });
    return this;
  }

  order(field: string): this {
    this.orderField = field;
    return this;
  }

  // -------------------------------------------------------------------------
  // Terminal methods
  // -------------------------------------------------------------------------

  /** Resolves the full result set. Used when the caller does `await builder`. */
  then<R>(
    resolve: (result: { data: Row[]; error: null }) => R,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _reject?: (reason: any) => R
  ): Promise<R> {
    return Promise.resolve(resolve({ data: this._execute(), error: null }));
  }

  /** Returns the first matching row or null. */
  async maybeSingle(): Promise<{ data: Row | null; error: null }> {
    const rows = this._execute();
    return { data: rows[0] ?? null, error: null };
  }

  // -------------------------------------------------------------------------
  // Internal
  // -------------------------------------------------------------------------

  private _execute(): Row[] {
    let result: Row[] = this.rows as Row[];

    for (const { field, value } of this.eqFilters) {
      result = result.filter((row) => row[field] === value);
    }

    if (this.orderField) {
      const f = this.orderField;
      result = [...result].sort((a, b) =>
        String(a[f] ?? "").localeCompare(String(b[f] ?? ""))
      );
    }

    return result.map((row) => projectRow(row, this.fields));
  }
}

// ---------------------------------------------------------------------------
// Public client
// ---------------------------------------------------------------------------
function from(table: string): QueryBuilder {
  const rows = TABLES[table];
  if (!rows) {
    console.error(`[db] Unknown table: "${table}"`);
    return new QueryBuilder([]);
  }
  return new QueryBuilder(rows);
}

export const db = { from };
